// 基本檔管理頁面 - Material UI 版本（支援拖拉排序）
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragHandle as DragHandleIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 可排序的列表項組件
function SortableItem({ id, item, index, onEdit, onDelete, isMobile }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
          boxShadow: 2,
        },
        boxShadow: isDragging ? 4 : 1,
      }}
      {...attributes}
    >
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            sx={{ cursor: 'grab', color: 'text.secondary' }}
            {...listeners}
          >
            <DragHandleIcon />
          </IconButton>
          
          <Typography
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: 'text.secondary',
              minWidth: 30,
              fontSize: 12
            }}
          >
            {index + 1}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{ flexGrow: 1, fontWeight: 500 }}
          >
            {item}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="編輯">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEdit(index, item)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="刪除">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const dataTypeConfig = {
  community: {
    title: '社團管理',
    label: '社團名稱',
    placeholder: '請輸入社團名稱'
  },
  department: {
    title: '社別/會別管理', 
    label: '社別/會別名稱',
    placeholder: '請輸入社別/會別名稱'
  },
  position: {
    title: '社職/會職管理',
    label: '社職/會職名稱', 
    placeholder: '請輸入社職/會職名稱'
  }
};

export default function BasicDataAdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // 從 URL 參數讀取分頁設定
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const dataTypes = Object.keys(dataTypeConfig);
      const tabIndex = dataTypes.indexOf(tabParam);
      return tabIndex >= 0 ? tabIndex : 0;
    }
    return 0;
  };
  
  const [currentTab, setCurrentTab] = useState(getInitialTab());
  const [data, setData] = useState({
    community: [],
    department: [],
    position: []
  });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, index: null });

  // 拖拉排序相關設定
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const dataTypes = Object.keys(dataTypeConfig);
  const currentType = dataTypes[currentTab];

  // 載入所有基本檔數據
  const fetchData = async () => {
    setLoading(true);
    try {
      const promises = dataTypes.map(async (type) => {
        const response = await fetch(`/api/basic-data/${type}`, { credentials: 'include' });
        const typeData = await response.json();
        return { type, data: typeData };
      });
      
      const results = await Promise.all(promises);
      const newData = {};
      results.forEach(({ type, data: typeData }) => {
        newData[type] = typeData;
      });
      
      setData(newData);
    } catch (error) {
      setSnackbar({ open: true, message: '載入資料失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 新增項目
  const handleAdd = () => {
    setEditing(null);
    setFormData({ name: '' });
    setModalOpen(true);
  };

  // 編輯項目
  const handleEdit = (index, item) => {
    setEditing(index);
    setFormData({ name: item });
    setModalOpen(true);
  };

  // 保存項目
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: '名稱不能為空', severity: 'warning' });
      return;
    }

    try {
      let response;
      if (editing !== null) {
        // 更新
        response = await fetch(`/api/basic-data/${currentType}/${editing}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: formData.name.trim() })
        });
      } else {
        // 新增
        response = await fetch(`/api/basic-data/${currentType}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: formData.name.trim() })
        });
      }

      const result = await response.json();
      
      if (response.ok) {
        setSnackbar({ 
          open: true, 
          message: editing !== null ? '更新成功' : '新增成功', 
          severity: 'success' 
        });
        
        // 更新本地數據
        setData(prev => ({
          ...prev,
          [currentType]: result.data
        }));
        
        setModalOpen(false);
        setEditing(null);
        setFormData({ name: '' });
      } else {
        setSnackbar({ open: true, message: result.error || '操作失敗', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '操作失敗', severity: 'error' });
    }
  };

  // 刪除項目
  const handleDelete = async (type, index) => {
    try {
      const response = await fetch(`/api/basic-data/${type}/${index}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
        
        // 更新本地數據
        setData(prev => ({
          ...prev,
          [type]: result.data
        }));
      } else {
        setSnackbar({ open: true, message: result.error || '刪除失敗', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
    
    setDeleteDialog({ open: false, type: null, index: null });
  };

  // 處理拖拉排序
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const currentData = data[currentType] || [];
    const oldIndex = currentData.findIndex((_, index) => `${currentType}-${index}` === active.id);
    const newIndex = currentData.findIndex((_, index) => `${currentType}-${index}` === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(currentData, oldIndex, newIndex);
      
      // 立即更新 UI
      setData(prev => ({
        ...prev,
        [currentType]: newOrder
      }));

      // 發送到後端保存
      try {
        const response = await fetch(`/api/basic-data/${currentType}/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ orderedData: newOrder })
        });

        if (response.ok) {
          setSnackbar({ open: true, message: '排序已保存', severity: 'success' });
        } else {
          // 如果保存失敗，還原順序
          setData(prev => ({
            ...prev,
            [currentType]: currentData
          }));
          setSnackbar({ open: true, message: '排序保存失敗', severity: 'error' });
        }
      } catch (error) {
        // 如果保存失敗，還原順序
        setData(prev => ({
          ...prev,
          [currentType]: currentData
        }));
        setSnackbar({ open: true, message: '排序保存失敗', severity: 'error' });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    const dataTypes = Object.keys(dataTypeConfig);
    const newTab = dataTypes[newValue];
    navigate(`/admin/basic-data?tab=${newTab}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const currentData = data[currentType] || [];
  const config = dataTypeConfig[currentType];

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
        基本檔管理
      </Typography>

      {/* 分頁選單 */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {dataTypes.map((type, index) => (
            <Tab 
              key={type} 
              label={dataTypeConfig[type].title}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* 操作按鈕 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              size={isMobile ? 'small' : 'medium'}
            >
              新增{config?.label?.replace('名稱', '')}
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              目前共有 {currentData.length} 個項目
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 可拖拉排序的資料列表 */}
      <Paper elevation={1} sx={{ p: 2 }}>
        {currentData.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              暫無{config?.label?.replace('名稱', '')}資料
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={currentData.map((_, index) => `${currentType}-${index}`)}
              strategy={verticalListSortingStrategy}
            >
              <Box>
                {currentData.map((item, index) => (
                  <SortableItem
                    key={`${currentType}-${index}`}
                    id={`${currentType}-${index}`}
                    item={item}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={(index) => setDeleteDialog({ open: true, type: currentType, index })}
                    isMobile={isMobile}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      {/* 新增/編輯對話框 */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing !== null ? '編輯' : '新增'}{config?.label?.replace('名稱', '')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={config?.label}
            placeholder={config?.placeholder}
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>
            取消
          </Button>
          <Button onClick={handleSave} variant="contained">
            {editing !== null ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, index: null })}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除「{deleteDialog.type && deleteDialog.index !== null ? 
              data[deleteDialog.type]?.[deleteDialog.index] : ''}」嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, index: null })}>
            取消
          </Button>
          <Button 
            onClick={() => handleDelete(deleteDialog.type, deleteDialog.index)}
            color="error"
            variant="contained"
          >
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}